<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import IndividualHeader from '@/modules/investing/components/individual-header.vue'
import IndividualNav from '@/modules/investing/components/individual-nav.vue'
import { VButton } from '@/modules/shared/components'
import { useRoute } from 'vue-router'
import TheFileSystem from '../../components/TheFileSystem.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useFilter } from '@/modules/core/composables/useFilter'
import { createQueryString } from '@/modules/shared/utils/routing'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import { useFilingStore } from '../../stores/filing-store'

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }
const { tab: queryTab } = useRoute().query as { tab: string }

const individualStore = useIndividualStore()
const filingStore = useFilingStore()
const theFileSystem = ref(null)
const { filters, setFilter } = useFilter({
  tab: queryTab || 'shared', // 'direct', 'shared'
})

const currentIndividual = computed(() => individualStore.individual)
const rootFolder = computed(() =>
  filingStore.folders.find((folder) => folder.id === currentIndividual.value?.filing_folder_root_id),
)

const selectedCids = computed(() => {
  if (individualStore.selectedShareholderKeys.length === individualStore.listShareholders.length) return null
  return individualStore.selectedShareholderKeys.map((keys) => JSON.parse(keys).reverse().join(':'))
})

const updateURLQuery = () => {
  const queryString = createQueryString(filters.value)
  window.history.replaceState(null, null, `?${queryString}`)
}

const selectTab = (tab: string) => {
  setFilter('tab', tab)
  updateURLQuery()
}

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetchAll = async () => {
  showSkeleton()
  await filingStore.resetStore()
  await individualStore.fetchIndividual(individual_id, { slug })
  if (filters.value.tab === 'direct') await filingStore.fetchFolder(currentIndividual.value.filing_folder_root_id)
  await theFileSystem.value?.fetchAll()
  hideSkeleton()
}

onMounted(async () => {
  await fetchAll()
  if (filters.value.tab === 'shared') individualStore.selectAllShareholderKeys()
})

const onFilter = async () => {
  showSkeleton()
  await fetchAll()
  hideSkeleton()
}

watch(
  () => filters.value.tab,
  async (newTab) => {
    if (newTab) {
      await individualStore.selectAllShareholderKeys()
      filingStore.resetStore()
      await fetchAll()
      if (newTab === 'shared') individualStore.selectAllShareholderKeys()
    }
  },
)
</script>

<template>
  <TheLayout>
    <IndividualHeader />
    <IndividualNav />
    <div class="mr-auto">
      <div class="mb-6 flex items-center justify-start space-x-1.5">
        <VButton :active="filters.tab === 'shared'" class="w-32" @click="selectTab('shared')">Shared Files</VButton>
        <VButton :active="filters.tab === 'direct'" class="w-32" @click="selectTab('direct')">Direct Files</VButton>
        <VSelectInvestor
          v-if="filters.tab === 'shared'"
          v-model="individualStore.selectedShareholderKeys"
          :investors="individualStore.listShareholders"
          :onClose="onFilter"
        />
      </div>
      <div class="mx-auto">
        <TheFileSystem
          :key="filters.tab"
          ref="theFileSystem"
          :skeleton="skeleton"
          :shareholders="[{ ...currentIndividual, type: 'Investor' }]"
          :shareholderCids="filters.tab === 'shared' ? selectedCids : null"
          :rootFolder="filters.tab === 'direct' ? rootFolder : null"
          :individual_id="filters.tab === 'shared' ? individual_id : null"
        />
      </div>
    </div>
  </TheLayout>
</template>
